import { Authorization, AuthorizationUnit } from '@/types';

/**
 * Per-vendor capability checks against GET /identity.
 *
 * `authorization.actions` is the union of every unit and must not gate a
 * control on one vendor. Match `authorization.units` to that vendor's bu.
 * Unit ids are canonical ("CA", "MEM"). A stored bu may be that id or a
 * catalog alias. The match is case-insensitive against the id and aliases.
 * A storage partition that is not an alias, such as MEM's "ce", does not match.
 * A blank vendor bu matches nothing, except vendors.update for a caller who
 * holds that action on exactly one unit: the operator directory only returns
 * that unit's vendors, and a missing bu must not hide Edit Vendor.
 * A global admin short-circuits true before units are read, because `units`
 * is empty when the admin holds no business-unit role.
 * `vendors.archive` is held per business unit by Manager and Developer. A global
 * admin may archive any vendor; everyone else must hold it on that vendor's unit.
 */

const READ_DIRECTORY_ACTIONS = ['vendors.read', 'vendors.update'] as const;

function holdsAction(actions: string[], action: string): boolean {
  return actions.some((entry) => entry.trim() === action);
}

function unitMatchesVendor(
  unit: AuthorizationUnit,
  vendorKey: string,
  catalog: Authorization['catalog'],
): boolean {
  if (unit.id.trim().toUpperCase() === vendorKey) {
    return true;
  }
  const entry = (catalog ?? []).find((item) => item.id === unit.id);
  return (entry?.aliases ?? []).some((alias) => alias.trim().toUpperCase() === vendorKey);
}

function matchingUnit(
  authorization: Authorization,
  vendorBu: string | null | undefined,
): AuthorizationUnit | undefined {
  const vendorKey = vendorBu?.trim().toUpperCase();
  if (!vendorKey) {
    return undefined;
  }
  return authorization.units.find((unit) =>
    unitMatchesVendor(unit, vendorKey, authorization.catalog),
  );
}

export function holdsForVendor(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
  action: string,
): boolean {
  if (!authorization) {
    return false;
  }
  if (authorization.globalAdmin) {
    return true;
  }
  return holdsAction(matchingUnit(authorization, vendorBu)?.actions ?? [], action);
}

export function canUpdateVendor(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
): boolean {
  if (holdsForVendor(authorization, vendorBu, 'vendors.update')) {
    return true;
  }
  if (!authorization || authorization.globalAdmin || vendorBu?.trim()) {
    return false;
  }
  const updaters = authorization.units.filter((unit) => holdsAction(unit.actions, 'vendors.update'));
  return updaters.length === 1;
}

export function canCreateVendor(
  authorization: Authorization | null | undefined,
): boolean {
  if (!authorization) {
    return false;
  }
  if (authorization.globalAdmin) {
    return true;
  }
  return authorization.units.some((unit) => holdsAction(unit.actions, 'vendors.create'));
}

export function canAssignVendorTag(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
): boolean {
  return holdsForVendor(authorization, vendorBu, 'vendors.assign_tag');
}

export function canManageTags(
  authorization: Authorization | null | undefined,
  businessUnitId: string | null | undefined,
): boolean {
  return holdsForVendor(authorization, businessUnitId, 'tags.manage');
}

export function canManageAnyTags(
  authorization: Authorization | null | undefined,
): boolean {
  if (!authorization) {
    return false;
  }
  if (authorization.globalAdmin) {
    return true;
  }
  return authorization.units.some((unit) => holdsAction(unit.actions, 'tags.manage'));
}

export function canUpdateVendorRouting(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
): boolean {
  return holdsForVendor(authorization, vendorBu, 'vendors.update_routing');
}

export function canArchiveVendor(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
): boolean {
  return holdsForVendor(authorization, vendorBu, 'vendors.archive');
}

export function canReadVendorDetail(
  authorization: Authorization | null | undefined,
  vendorBu: string | null | undefined,
): boolean {
  if (!authorization) {
    return false;
  }
  if (authorization.globalAdmin) {
    return true;
  }
  const unit = matchingUnit(authorization, vendorBu);
  if (!unit) {
    return false;
  }
  return READ_DIRECTORY_ACTIONS.some((action) => holdsAction(unit.actions, action));
}

/**
 * Route entry for the operator directory. The guard does not know which
 * vendor will be opened, so this is true when any unit holds vendors.read
 * or vendors.update. It does not treat globalAdmin as access: OperatorOnlyRoute
 * rejects unrestricted callers before this runs.
 */
export function canEnterOperatorVendorDirectory(
  authorization: Authorization | null | undefined,
): boolean {
  if (!authorization) {
    return false;
  }
  return authorization.units.some((unit) =>
    READ_DIRECTORY_ACTIONS.some((action) => holdsAction(unit.actions, action)),
  );
}
